import { useCallback, useMemo, useState } from "react";

import { orderBy } from "lodash";

import {
  ClientId,
  PortfolioPlanAssignmentId,
  PortfolioPlanId,
  SecurityId,
} from "@fartherfinance/frontend/api/Types";

import {
  ExclusionsList,
  markAllSecuritiesHelper,
  unmarkAllSecuritiesHelper,
} from "../helpers";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { typedEntries } from "@src/multiCustodian/utils/arrayHelpers";

import usePortfolioHoldingsWithExcludedSecurities from "./usePortfolioHoldingsWithExcludedSecurities";

/**
 * Hook to manage exclusions functionality for the "Do not sell" feature
 * @param clientId The client ID
 * @param portfolioPlanId The portfolio plan ID
 * @param assignmentId The portfolio plan assignment ID
 */
const usePortfolioHoldingsWithExclusions = (
  clientId: ClientId,
  portfolioPlanId: PortfolioPlanId,
  assignmentId: PortfolioPlanAssignmentId
) => {
  // State to track local changes to exclusions
  const [localExclusions, setLocalExclusions] = useState<ExclusionsList>({});

  const clientAuth = useRequestAuth();

  // Get portfolio holdings with excluded securities from API
  const {
    data,
    isLoading,
    hasError: apiError,
  } = usePortfolioHoldingsWithExcludedSecurities(
    {
      clientId,
      portfolioPlanId,
      portfolioPlanAssignmentId: assignmentId,
    },
    clientAuth
  );

  // Always start with empty localExclusions

  // Toggle an exclusion
  const toggleExclusion = useCallback(
    (securityId: SecurityId, onOrOff: boolean) => {
      setLocalExclusions((prev) => {
        // If turning off and exists in list, remove it
        if (!onOrOff && prev[securityId] !== undefined) {
          const { [securityId]: _, ...rest } = { ...prev };
          return rest;
        }

        return {
          ...prev,
          [securityId]: onOrOff,
        };
      });
    },
    []
  );

  // Mark all securities as excluded - use helper function
  const markAllSecurities = useCallback(() => {
    if (!data) {
      return;
    }

    const markedSecurities = markAllSecuritiesHelper(data);
    setLocalExclusions(markedSecurities);
  }, [data]);

  // Unmark all securities - use helper function
  const unmarkAllSecurities = useCallback(() => {
    if (!data) {
      return;
    }

    const unmarkedSecurities = unmarkAllSecuritiesHelper(data);
    setLocalExclusions(unmarkedSecurities);
  }, [data]);

  // Calculate changes between API and local state
  const numberOfChanges = useMemo(() => {
    if (!data) {
      return 0;
    }

    return typedEntries(localExclusions).length;
  }, [data, localExclusions]);

  // Sort list first by exclusion status, then alphabetically by ticker
  const sortedList = orderBy(
    data,
    [
      // First sorting criteria: exclusion status (portfolio > excluded > not excluded)
      (sec) =>
        sec.isInPortfolio
          ? SORT_PRIORITY.isInPortfolio
          : sec.isExcluded
          ? SORT_PRIORITY.isExcluded
          : SORT_PRIORITY.isNotExcluded,
      // Second sorting criteria: ticker/name alphabetically
      (sec) => (sec.ticker || "ZZZ").toLowerCase(),
    ],
    ["desc", "asc"] // First criteria descending, second criteria ascending
  );

  return {
    // Data
    securities: sortedList,
    localExclusions,
    setLocalExclusions,

    // Loading states
    isLoading,

    // Error state
    error: apiError,
    hasError: Boolean(apiError),

    // Actions
    toggleExclusion,
    markAllSecurities,
    unmarkAllSecurities,

    // Metrics
    numberOfChanges,
  };
};

export default usePortfolioHoldingsWithExclusions;

const SORT_PRIORITY = {
  isExcluded: "2",
  isNotExcluded: "1",
  isInPortfolio: "0",
} as const;
